import {
  UnreadCaseWithLastMessage,
  GetSupportWidgetMqttConnectionInfoResponse,
} from '@provider-portal/common/api/openapi_gen/deliveryProviderPortal/deliveryProviderPortal/apis';
import { ActionType, getType } from 'typesafe-actions';

import { logoutAsync, NetworkRequestState } from '@bolteu/redux-session';

import {
  getMainEntryWidgetUrlAsyncAction,
  getSupportCaseWidgetUrlAsyncAction,
  getSupportWidgetMqttConnectionInfoAsyncAction,
  getUnreadCasesWithLastMessageAsyncAction,
  resetSelectedSupportCaseAction,
  resetSupportWidgetDataAction,
  setSelectedSupportCaseAction,
  SupportActions,
} from './actions';

export interface SelectedSupportCase {
  case_id: number;
  provider_id: number;
}

export interface SupportState {
  mainEntryWidgetUrl: string;
  supportCaseUrl: string;
  unreadCases: UnreadCaseWithLastMessage[];
  mqttConnectionInfo: GetSupportWidgetMqttConnectionInfoResponse;
  fetchingMainEntryWidgetUrlState: NetworkRequestState;
  fetchingSupportCaseUrlState: NetworkRequestState;
  fetchingUnreadCasesState: NetworkRequestState;
  fetchingMqttConnectionInfo: NetworkRequestState;
  selectedCase: SelectedSupportCase;
}

const initialState: SupportState = {
  mainEntryWidgetUrl: '',
  supportCaseUrl: '',
  unreadCases: [],
  mqttConnectionInfo: { broker_url: '', user_id: 0 },
  fetchingMainEntryWidgetUrlState: NetworkRequestState.unset,
  fetchingSupportCaseUrlState: NetworkRequestState.unset,
  fetchingUnreadCasesState: NetworkRequestState.unset,
  fetchingMqttConnectionInfo: NetworkRequestState.unset,
  selectedCase: undefined,
};

type AdditionalReduxActions = {
  logoutAsync: typeof logoutAsync;
};

export const supportReducer = (
  state: SupportState = initialState,
  action: SupportActions | ActionType<AdditionalReduxActions>,
): SupportState => {
  switch (action.type) {
    case getType(getMainEntryWidgetUrlAsyncAction.request): {
      return {
        ...state,
        fetchingMainEntryWidgetUrlState: NetworkRequestState.waiting,
      };
    }
    case getType(getMainEntryWidgetUrlAsyncAction.success): {
      return {
        ...state,
        mainEntryWidgetUrl: action.payload.url,
        fetchingMainEntryWidgetUrlState: NetworkRequestState.success,
      };
    }
    case getType(getMainEntryWidgetUrlAsyncAction.failure): {
      return {
        ...state,
        fetchingMainEntryWidgetUrlState: NetworkRequestState.failure,
      };
    }
    case getType(getSupportCaseWidgetUrlAsyncAction.request): {
      return {
        ...state,
        fetchingSupportCaseUrlState: NetworkRequestState.waiting,
      };
    }
    case getType(getSupportCaseWidgetUrlAsyncAction.success): {
      return {
        ...state,
        supportCaseUrl: action.payload.url,
        fetchingSupportCaseUrlState: NetworkRequestState.success,
      };
    }
    case getType(getSupportCaseWidgetUrlAsyncAction.failure): {
      return {
        ...state,
        fetchingSupportCaseUrlState: NetworkRequestState.failure,
      };
    }
    case getType(getUnreadCasesWithLastMessageAsyncAction.request): {
      return {
        ...state,
        fetchingUnreadCasesState: NetworkRequestState.waiting,
      };
    }
    case getType(getUnreadCasesWithLastMessageAsyncAction.success): {
      return {
        ...state,
        unreadCases: action.payload.cases,
        fetchingUnreadCasesState: NetworkRequestState.success,
      };
    }
    case getType(getUnreadCasesWithLastMessageAsyncAction.failure): {
      return {
        ...state,
        fetchingUnreadCasesState: NetworkRequestState.failure,
      };
    }
    case getType(getSupportWidgetMqttConnectionInfoAsyncAction.request): {
      return {
        ...state,
        fetchingMqttConnectionInfo: NetworkRequestState.waiting,
      };
    }
    case getType(getSupportWidgetMqttConnectionInfoAsyncAction.success): {
      return {
        ...state,
        mqttConnectionInfo: {
          broker_url: action.payload.broker_url,
          user_id: action.payload.user_id,
        },
        fetchingMqttConnectionInfo: NetworkRequestState.success,
      };
    }
    case getType(getSupportWidgetMqttConnectionInfoAsyncAction.failure): {
      return {
        ...state,
        fetchingMqttConnectionInfo: NetworkRequestState.failure,
      };
    }
    case getType(logoutAsync.success):
    case getType(resetSupportWidgetDataAction): {
      return {
        ...state,
        mainEntryWidgetUrl: '',
        supportCaseUrl: '',
        unreadCases: [],
        mqttConnectionInfo: { broker_url: '', user_id: 0 },
        fetchingMainEntryWidgetUrlState: NetworkRequestState.unset,
        fetchingSupportCaseUrlState: NetworkRequestState.unset,
        fetchingUnreadCasesState: NetworkRequestState.unset,
        fetchingMqttConnectionInfo: NetworkRequestState.unset,
      };
    }
    case getType(setSelectedSupportCaseAction): {
      return {
        ...state,
        selectedCase: { case_id: action.payload.case_id, provider_id: action.payload.provider_id },
      };
    }
    case getType(resetSelectedSupportCaseAction): {
      return {
        ...state,
        selectedCase: undefined,
      };
    }

    default: {
      return state;
    }
  }
};
